// AdminManageStoreItems: Admin UI for add/update/delete store items
import React, { useEffect, useState } from "react";
import {
	Box,
	Typography,
	Button,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	TextField,
	Select,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	CircularProgress,
	Input,
	Chip,
	Tooltip,
	IconButton,
} from "@mui/material";
import { QrCode, Refresh } from "@mui/icons-material";
import supabase from "../../../supabaseClient";
import BarcodeDisplay from "../../../Components/BarcodeDisplay";

export default function AdminManageStoreItems() {
	const [items, setItems] = useState([]);
	const [seasons, setSeasons] = useState([]);
	const [loading, setLoading] = useState(true);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [editItem, setEditItem] = useState(null);
	const [form, setForm] = useState({
		title: "",
		type: "",
		season_id: "",
		price_cents: "",
		active: true,
		description: "",
		sku: "",
		barcode: "",
		quantity_on_hand: 0,
		reorder_level: 0,
	});
	const [photoFile, setPhotoFile] = useState(null);
	const [barcodeDialogItem, setBarcodeDialogItem] = useState(null);

	useEffect(() => {
		supabase
			.from("store_items")
			.select("*")
			.then(({ data }) => setItems(data || []));
		supabase
			.from("seasons")
			.select("id, name")
			.then(({ data }) => setSeasons(data || []));
		setLoading(false);
	}, []);

	const generateBarcode = (item) => {
		const newBarcode =
			"GG-" + String(item.id).replace(/-/g, "").slice(0, 12).toUpperCase();
		return newBarcode;
	};

	const handleRegenerateBarcode = async (item) => {
		const barcode = generateBarcode(item);
		await supabase.from("store_items").update({ barcode }).eq("id", item.id);
		supabase
			.from("store_items")
			.select("*")
			.then(({ data }) => setItems(data || []));
		setBarcodeDialogItem({ ...item, barcode });
	};

	const handleOpen = (item = null) => {
		setEditItem(item);
		setForm(
			item
				? {
						...item,
						title: item.title || item.name,
						name: undefined,
						price_cents: (item.price_cents / 100).toFixed(2),
						sku: item.sku || "",
						barcode: item.barcode || "",
						quantity_on_hand: item.quantity_on_hand ?? 0,
						reorder_level: item.reorder_level ?? 0,
					}
				: {
						title: "",
						type: "",
						season_id: "",
						price_cents: "",
						active: true,
						description: "",
						sku: "",
						barcode: "",
						quantity_on_hand: 0,
						reorder_level: 0,
					},
		);
		setDialogOpen(true);
	};

	const handleClose = () => {
		setDialogOpen(false);
		setEditItem(null);
	};

	const handleChange = (e) => {
		setForm({ ...form, [e.target.name]: e.target.value });
	};

	const handlePhotoChange = (e) => {
		if (e.target.files && e.target.files[0]) {
			setPhotoFile(e.target.files[0]);
		}
	};

	const handleSave = async () => {
		setLoading(true);
		let photo_url = form.photo_url || "";
		let result;
		try {
			const { data } = await supabase.auth.getUser();
			console.log(data.user);
			if (photoFile) {
				const fileExt = photoFile.name.split(".").pop();
				const fileName = `storeitem-${Date.now()}.${fileExt}`;
				const { data: uploadData, error: uploadError } = await supabase.storage
					.from("store_photos")
					.upload(`public/${fileName}`, photoFile, { upsert: true });
				if (uploadError) throw uploadError;
				const { data: publicUrlData } = supabase.storage
					.from("store_photos")
					.getPublicUrl(uploadData.path);
				photo_url = publicUrlData.publicUrl;
			}
			const payload = {
				...form,
				price_cents: Math.round(Number(form.price_cents) * 100),
				photo_url,
				sku: form.sku || null,
				barcode: form.barcode || null,
				quantity_on_hand: parseInt(form.quantity_on_hand, 10) || 0,
				reorder_level: parseInt(form.reorder_level, 10) || 0,
			};

			if (editItem) {
				result = await supabase
					.from("store_items")
					.update(payload)
					.eq("id", editItem.id);
			} else {
				result = await supabase.from("store_items").insert([payload]);
			}
			if (result.error) {
				alert("Error saving item: " + result.error.message);
			} else {
				supabase
					.from("store_items")
					.select("*")
					.then(({ data }) => setItems(data || []));
				handleClose();
			}
			setLoading(false);
		} catch (err) {
			setLoading(false);
			alert("Error saving item: " + err.message);
		}
	};

	const handleDelete = async (id) => {
		setLoading(true);
		try {
			const result = await supabase.from("store_items").delete().eq("id", id);
			if (result.error) {
				alert("Error deleting item: " + result.error.message);
			} else {
				supabase
					.from("store_items")
					.select("*")
					.then(({ data }) => setItems(data || []));
			}
			setLoading(false);
		} catch (err) {
			setLoading(false);
			alert("Error deleting item: " + err.message);
		}
	};

	return (
		<Box sx={{ mx: "auto", width: "100%" }}>
			<Typography variant="h5" gutterBottom>
				Manage Store Items
			</Typography>
			<Button
				variant="contained"
				color="primary"
				onClick={() => handleOpen()}
				sx={{ mb: 2 }}
			>
				Add New Item
			</Button>
			{loading ? (
				<CircularProgress />
			) : (
				<Box sx={{ width: "100%", overflowX: "auto" }}>
					<Table size="small" sx={{ minWidth: 500, width: "100%" }}>
						<TableHead>
							<TableRow>
								<TableCell sx={{ minWidth: 100 }}>Title</TableCell>
								<TableCell sx={{ minWidth: 80 }}>SKU</TableCell>
								<TableCell sx={{ minWidth: 80 }}>Barcode</TableCell>
								<TableCell sx={{ minWidth: 100 }}>Type</TableCell>
								<TableCell sx={{ minWidth: 120 }}>Season</TableCell>
								<TableCell sx={{ minWidth: 80 }}>Price</TableCell>
								<TableCell sx={{ minWidth: 70 }}>On Hand</TableCell>
								<TableCell sx={{ minWidth: 70 }}>Reorder</TableCell>
								<TableCell sx={{ minWidth: 80 }}>Active</TableCell>
								<TableCell sx={{ minWidth: 120 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{items.map((item) => (
								<TableRow key={item.id}>
									<TableCell>{item.title}</TableCell>
									<TableCell>{item.sku || "—"}</TableCell>
									<TableCell>
										{item.barcode ? (
											<Chip
												label={item.barcode}
												size="small"
												onClick={() => setBarcodeDialogItem(item)}
												sx={{ cursor: "pointer" }}
											/>
										) : (
											<Tooltip title="Generate barcode">
												<IconButton
													size="small"
													onClick={() => handleRegenerateBarcode(item)}
												>
													<QrCode fontSize="small" />
												</IconButton>
											</Tooltip>
										)}
									</TableCell>
									<TableCell>{item.type}</TableCell>
									<TableCell>
										{seasons.find((s) => s.id === item.season_id)?.name ||
											item.season_id}
									</TableCell>
									<TableCell>${(item.price_cents / 100).toFixed(2)}</TableCell>
									<TableCell>{item.quantity_on_hand ?? 0}</TableCell>
									<TableCell>{item.reorder_level ?? 0}</TableCell>
									<TableCell>{item.active ? "Yes" : "No"}</TableCell>
									<TableCell>
										<Button size="small" onClick={() => handleOpen(item)}>
											Edit
										</Button>
										<Button
											size="small"
											color="error"
											onClick={() => handleDelete(item.id)}
										>
											Delete
										</Button>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</Box>
			)}
			<Dialog
				open={dialogOpen}
				onClose={handleClose}
				fullWidth
				maxWidth="sm"
				sx={{
					"& .MuiDialog-paper": {
						width: "100%",
						maxWidth: 400,
						m: 1,
					},
				}}
			>
				<DialogTitle>
					{editItem ? "Edit Store Item" : "Add Store Item"}
				</DialogTitle>
				<DialogContent sx={{ p: 1 }}>
					<TextField
						label="Title"
						name="title"
						value={form.title}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
					/>

					<TextField
						label="Price"
						name="price_cents"
						value={form.price_cents}
						onChange={handleChange}
						type="number"
						fullWidth
						margin="normal"
						size="small"
					/>

					<TextField
						label="Description"
						name="description"
						value={form.description}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
					/>
					<Box mt={2}>
						<Typography variant="subtitle2" gutterBottom>
							Type
						</Typography>
						<Select
							name="type"
							value={form.type}
							onChange={handleChange}
							fullWidth
							displayEmpty
							size="small"
						>
							<MenuItem value="">Select Type</MenuItem>
							<MenuItem value="preorder">Preorder</MenuItem>
							<MenuItem value="in_stock">In Stock</MenuItem>
							<MenuItem value="bundle">Bundle</MenuItem>
						</Select>
					</Box>
					<Box mt={2}>
						<Typography variant="subtitle2" gutterBottom>
							Season
						</Typography>
						<Select
							name="season_id"
							value={form.season_id || ""}
							onChange={handleChange}
							fullWidth
							displayEmpty
							size="small"
						>
							{seasons.length === 0 ? (
								<MenuItem value="" disabled>
									No Seasons Available
								</MenuItem>
							) : (
								seasons.map((s) => (
									<MenuItem key={s.id} value={s.id}>
										{s.name}
									</MenuItem>
								))
							)}
						</Select>
					</Box>
					<Box mt={2}>
						<Typography variant="subtitle2" gutterBottom>
							Active
						</Typography>
						<Select
							name="active"
							value={form.active}
							onChange={handleChange}
							fullWidth
							size="small"
						>
							<MenuItem value={true}>Yes</MenuItem>
							<MenuItem value={false}>No</MenuItem>
						</Select>
					</Box>
					<Box mt={2}>
						<Typography variant="subtitle2" gutterBottom>
							Photo
						</Typography>
						<Input
							type="file"
							inputProps={{ accept: "image/*" }}
							onChange={handlePhotoChange}
							fullWidth
						/>
					</Box>
					<TextField
						label="SKU"
						name="sku"
						value={form.sku}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
						helperText="Optional unique product code"
					/>
					<TextField
						label="Barcode"
						name="barcode"
						value={form.barcode}
						onChange={handleChange}
						fullWidth
						margin="normal"
						size="small"
						helperText="Leave blank to auto-generate"
					/>
					<TextField
						label="Quantity on Hand"
						name="quantity_on_hand"
						value={form.quantity_on_hand}
						onChange={handleChange}
						type="number"
						fullWidth
						margin="normal"
						size="small"
					/>
					<TextField
						label="Reorder Level"
						name="reorder_level"
						value={form.reorder_level}
						onChange={handleChange}
						type="number"
						fullWidth
						margin="normal"
						size="small"
						helperText="Show Low Stock warning when on-hand reaches this level"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={handleClose} disabled={loading}>
						Cancel
					</Button>
					<Button
						onClick={handleSave}
						variant="contained"
						color="primary"
						disabled={loading}
					>
						{loading ? <CircularProgress size={20} /> : "Save"}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Barcode viewer dialog */}
			<Dialog
				open={!!barcodeDialogItem}
				onClose={() => setBarcodeDialogItem(null)}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>Barcode — {barcodeDialogItem?.title}</DialogTitle>
				<DialogContent
					sx={{
						display: "flex",
						flexDirection: "column",
						alignItems: "center",
						py: 3,
					}}
				>
					<BarcodeDisplay
						value={barcodeDialogItem?.barcode}
						label={barcodeDialogItem?.title}
						sku={barcodeDialogItem?.sku}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						startIcon={<Refresh />}
						onClick={() => handleRegenerateBarcode(barcodeDialogItem)}
						size="small"
					>
						Regenerate
					</Button>
					<Button onClick={() => setBarcodeDialogItem(null)}>Close</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}
